import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import {
    Bold,
    Italic,
    Strikethrough,
    Heading1,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    Quote as QuoteIcon,
    Undo2,
    Redo2,
    Link2,
    Image as ImageIcon,
    Code,
    Pilcrow,
} from "lucide-react";

export default function RichTextEditor({ value, onChange, placeholder, testId }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
            Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", class: "text-teal-700 underline" } }),
            Image,
        ],
        content: value || "",
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: "prose prose-sm max-w-none px-4 py-3 focus:outline-none min-h-[260px]",
                "data-testid": testId || "rich-editor",
            },
        },
    });

    // Sync external value changes
    useEffect(() => {
        if (!editor) return;
        const currentHtml = editor.getHTML();
        if (value !== undefined && value !== currentHtml) {
            editor.commands.setContent(value || "", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, editor]);

    if (!editor) {
        return (
            <div className="rounded-md border border-slate-300 px-3 py-3 text-sm text-slate-500">
                Editör yükleniyor...
            </div>
        );
    }

    const Btn = ({ Icon, onClick, active, label }) => (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className={`p-1.5 rounded hover:bg-light-bg ${active ? "bg-dark-navy text-white" : "text-deep-blue"}`}
        >
            <Icon className="w-3.5 h-3.5" />
        </button>
    );

    const setLink = () => {
        const url = window.prompt("URL girin:");
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };
    const setImage = () => {
        const url = window.prompt("Görsel URL'si:");
        if (!url) return;
        editor.chain().focus().setImage({ src: url }).run();
    };

    return (
        <div className="rounded-md border border-slate-300 overflow-hidden bg-white" data-testid={`${testId || "editor"}-wrapper`}>
            <div className="flex items-center gap-1 flex-wrap border-b border-slate-200 bg-light-bg/60 px-2 py-1.5">
                <Btn Icon={Bold} active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} label="Bold" />
                <Btn Icon={Italic} active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italic" />
                <Btn Icon={Strikethrough} active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} label="Strikethrough" />
                <span className="w-px h-4 bg-slate-300 mx-1" />
                <Btn Icon={Heading1} active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} label="H1" />
                <Btn Icon={Heading2} active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} label="H2" />
                <Btn Icon={Heading3} active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} label="H3" />
                <Btn Icon={Pilcrow} active={editor.isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()} label="Paragraph" />
                <span className="w-px h-4 bg-slate-300 mx-1" />
                <Btn Icon={List} active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Bullet list" />
                <Btn Icon={ListOrdered} active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Ordered list" />
                <Btn Icon={QuoteIcon} active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} label="Quote" />
                <Btn Icon={Code} active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} label="Code" />
                <span className="w-px h-4 bg-slate-300 mx-1" />
                <Btn Icon={Link2} active={editor.isActive("link")} onClick={setLink} label="Link" />
                <Btn Icon={ImageIcon} onClick={setImage} label="Image" />
                <span className="w-px h-4 bg-slate-300 mx-1" />
                <Btn Icon={Undo2} onClick={() => editor.chain().focus().undo().run()} label="Undo" />
                <Btn Icon={Redo2} onClick={() => editor.chain().focus().redo().run()} label="Redo" />
            </div>
            <EditorContent editor={editor} />
        </div>
    );
}
